import React from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useSelector } from "react-redux";
import { useRoutes, useNavigate } from "react-router-dom";
import "./App.css";
import { RTL } from "./layout/fullLayout/customizer/RTL";
import { ThemeSettings } from "./layout/fullLayout/customizer/ThemeSettings";
import { IStoreState } from "./redux/initialStoreState";
import { Router } from "./router/router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  DataTableV2Provider,
  DataTableV2ProviderHandle,
} from "./components/TableV2/context/DataTableV2Provider";
import { api } from "./api/api";
import { useAuth } from "./contexts/AuthProvider/AuthProvider";
import { navigationService } from "./services/navigationService";
import { WhatsAppStatusProvider } from "./contexts/WhatsAppStatusProvider";
import { MessageBox } from "./components/MessageBox/MessageBox";
import { Helmet } from "react-helmet-async";
import { config } from "./config";

function App() {
  const routing = useRoutes(Router);
  const navigate = useNavigate();
  const theme = ThemeSettings();
  const customizer = useSelector(
    (state: IStoreState) => state.customizerReducer,
  );
  const ref = React.useRef<DataTableV2ProviderHandle | null>(null);
  // Per-module AbortControllers for in-flight upsert-user-meta-config requests.
  // Keyed by module_name so a newer save for a module cancels only the previous
  // save of that same module (not other modules' saves).
  const upsertAbortRef = React.useRef<Record<string, AbortController>>({});
  const { user, isLogin, token } = useAuth();

  React.useEffect(() => {
    navigationService.setNavigateFunction(navigate);
  }, [navigate]);

  // LOAD: the API returns one row per table/module —
  //   [{ module_name, config_value: <bare table state> }, ...]
  // Convert it into a store keyed by module_name:
  //   { EVENT_LEADS: {...}, CHAT_LEADS: {...} }
  const getCachingData = async () => {
    try {
      const res = await api.get(
        "/user/get-user-meta-config?user_uuid=" + user.userUuid,
      );
      const rows: any[] = res.data?.data || [];
      return rows.reduce((acc: Record<string, any>, row: any) => {
        if (row?.module_name) {
          acc[row.module_name] = row.config_value;
        }
        return acc;
      }, {});
    } catch (err) {
      console.log(err);
      return {};
    }
  };

  // SAVE: one upsert per module. `config_value` is the bare table state for
  // that single module (no wrapper key). Cancels the previous in-flight save
  // for the SAME module only.
  const saveCachingData = async (moduleName: string, configValue: any) => {
    if (!moduleName) return;
    upsertAbortRef.current[moduleName]?.abort();
    const controller = new AbortController();
    upsertAbortRef.current[moduleName] = controller;
    try {
      await api.post(
        "/user/upsert-user-meta-config",
        {
          user_uuid: user.userUuid,
          module_name: moduleName,
          config_value: configValue,
        },
        { signal: controller.signal },
      );
    } catch (err: any) {
      // Ignore cancellations from a newer save superseding this one.
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }
      console.log(err);
    }
  };

  React.useEffect(() => {
    // Load the user's saved table config from the server on login.
    // Note: this must NOT depend on `subscribed_plan` — that was blocking the
    // call for users whose token has no plan, so filters never reloaded.
    if (isLogin && token && user?.userUuid) {
      ref.current?.getCachingConfigFromServerAtLoad?.();
    }
  }, [isLogin, token, user?.userUuid]);

 
  return (
    <>
      <Helmet>
        <title>{config.app.title}</title>
        <meta
          name="description"
          content={ config.app.subTitle}
        />
      </Helmet>

      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <RTL direction={customizer.activeDir as "ltr"}>
            <DataTableV2Provider
              ref={ref}
              cachingFilters={{
                enableCaching: true,
                getCachingConfigFromServerAtLoad: getCachingData,
                saveCachingIntoServer: saveCachingData,
              }}
            >
              <WhatsAppStatusProvider>
                <CssBaseline />

                {routing}
              </WhatsAppStatusProvider>
            </DataTableV2Provider>
          </RTL>
        </LocalizationProvider>
        <MessageBox />
      </ThemeProvider>
    </>
  );
}

export default App;
